/* eslint-disable camelcase */
import React, { FC, useCallback, useEffect, useState } from "react"
import { observer } from "mobx-react-lite"
import { Appearance, FlatList, ListRenderItem, ListRenderItemInfo, useWindowDimensions, ViewStyle } from "react-native"
import { ListEmpty, Screen, Text } from "../../../../components"
// import { useStores } from "../../models"
import { color, nativeBaseTheme } from "../../../../theme"
import { useStores } from "../../../../models"
import { FarmStoreModel } from "../../../../models/app-store/farm-store/farm-store"
import { BatchStoreModel } from "../../../../models/app-store/farm-store/batch-store/batch-store"
import { View } from "native-base"
import { SafeAreaView } from "react-native-safe-area-context"
import numeral from "numeral"
import { translate } from "../../../../i18n"
import { InfoItem, ItemFooter, InfoWarningModal } from "../overview-feed-cost-list/overview-feed-cost-list-screen"
import { InfoModal } from "../overview-production-cost-estimation-list/overview-production-cost-estimation-list-screen"
import { Currency } from "../../../../database"
import JalaAnalytic from "../../../../utils/jala-analytic"
import { useFocusEffect } from "@react-navigation/native"
import { getSnapshot } from "mobx-state-tree"

const ROOT: ViewStyle = {
  backgroundColor: color.palette.white,
  flex: 1,
}

const HEADER: ViewStyle = {
  borderTopWidth: 1,
  borderBottomWidth: 1,
  borderColor: color.palette.gray200,
  flexDirection: 'row',
  paddingVertical: 8,
  alignItems: 'center'
}

const CONTENT_ITEM: ViewStyle = {
  borderBottomWidth: 1,
  borderColor: color.palette.gray200,
  flexDirection: 'row'
}

const Header: FC<{ colorScheme?: "light"|"dark" }> = observer(function Header(props) {
  const { colorScheme = "light" || Appearance.getColorScheme() } = props;
  const { appStore: { farmStore }} = useStores();
  const { activeBatchStore } = farmStore || FarmStoreModel.create({})
  const { predictionRevenue, predictionTotalRoughCost, cycles, isFeedActualWithin4Days, loadingCyclePrediction } = activeBatchStore || BatchStoreModel.create({})

  return (
    <View paddingX="16px" paddingTop="8px" paddingBottom="15px">
      <Text text={farmStore.farm.name} preset="title3" marginBottom="4px" colorScheme={colorScheme} />
      <Text tx="overviewProfitPotentialListScreen.subtitle" preset="body3" colorScheme={colorScheme} color={nativeBaseTheme.colors.gray[colorScheme === 'light' ? '700' : '300']} />
      <View flexDirection="row" alignItems="center">
        <Text
          text={numeral((predictionRevenue - predictionTotalRoughCost) / 1000000).format('0,0.[00]')}
          preset="display1"
          marginRight="3px"
          colorScheme={colorScheme}
          color={loadingCyclePrediction && color.palette.dust500 || undefined}
        />
        <Text tx="million" preset="display2" marginRight="8px" colorScheme={colorScheme} color={color.palette.dust500} />
      </View>
      <Text text={translate("overviewScreen.fromActivePonds", { count: cycles.length })} preset="body3" colorScheme={colorScheme} color={color.palette.dust500} />
      {!isFeedActualWithin4Days && <InfoWarningModal />}
    </View>
  )
})

const TableHeader: FC<{ colorScheme?: "light"|"dark", currency: string }> = (props) => {
  const { currency } = props;

  return (
    <View style={HEADER}>
      <View width="25%" paddingX="8px" alignItems="flex-start">
        <Text tx="overviewProfitPotentialListScreen.pond" preset="body3" txOptions={{currency}} color={color.palette.gray800} />
      </View>
      <View width="25%" paddingX="8px" alignItems="flex-end">
        <Text textAlign={"right"} tx="overviewProfitPotentialListScreen.estProfit" preset="body3" txOptions={{currency}} color={color.palette.gray800} />
      </View>
      <View width="25%" paddingX="8px" alignItems="flex-end">
        <Text textAlign={"right"} tx="overviewProfitPotentialListScreen.estRevenue" preset="body3" txOptions={{currency}} color={color.palette.gray800} />
      </View>
      <View width="25%" paddingX="8px" alignItems="flex-end">
        <Text textAlign={"right"} tx="overviewProfitPotentialListScreen.estProductionCost" preset="body3" txOptions={{currency}} color={color.palette.gray800} />
      </View>
    </View>
  )
}

const Item: FC<{ colorScheme?: "light" | "dark", info: ListRenderItemInfo<any> }> = observer(function Item(props) {
  const { colorScheme, info } = props
  const [potentialRevenue, setPotentialRevenue] = useState(0)
  const [actualCycle, setActualCycle] = useState<any>(0)

  const { item: { age, pond_name, cycle_actuals, cycle_predictions} } = info;
  useEffect(() => {
    const actualCycle = cycle_actuals.find((c) => c.age === age) || {}
    setActualCycle(actualCycle)
    if (actualCycle.potential_revenue) {
      setPotentialRevenue(actualCycle.potential_revenue)
    } else {
      const predictionCycle = cycle_predictions.find((c) => c.age === age) || {}
      setPotentialRevenue(predictionCycle.potential_revenue)
    }
  }, [age, cycle_actuals, cycle_predictions])

  return (
    <View style={CONTENT_ITEM}>
      <View width="25%" paddingX="8px" py="10px" alignItems="flex-start">
        <Text ellipsizeMode="middle" text={`${pond_name} (${age})`} preset="body3" colorScheme={colorScheme} numberOfLines={1} />
      </View>
      <View width="25%" paddingX="8px" py="10px" alignItems="flex-end" backgroundColor={color.palette.gray100}>
        <Text text={numeral(potentialRevenue - actualCycle?.rough_total_cost).format('0,0')} preset="body3" colorScheme={colorScheme} />
      </View>
      <View width="25%" paddingX="8px" py="10px" alignItems="flex-end">
        <Text text={numeral(potentialRevenue).format('0,0')} preset="body3" colorScheme={colorScheme} />
      </View>
      <View width="25%" paddingX="8px" py="10px" alignItems="flex-end">
        <Text text={numeral(actualCycle?.rough_total_cost).format('0,0')} preset="body3" colorScheme={colorScheme} />
      </View>
    </View>
  )
});

export const OverviewProfitPotentialListScreen: FC<{ colorScheme?: "light" | "dark" }> = observer(function OverviewProfitPotentialListScreen(props) {
  const { colorScheme = "light" || Appearance.getColorScheme() } = props;
  const { height } = useWindowDimensions();

  // Pull in one of our MST stores
  const { appStore: { farmStore }} = useStores();
  const { activeBatchStore, farm } = farmStore || FarmStoreModel.create({})
  const { cycles, loadingCyclePrediction } = activeBatchStore || BatchStoreModel.create({})
  const keyExtractor = useCallback((item, index) => `${item.id || item.started_at || index}`, [])
  const [isOpenInfo, setOpenInfo] = useState<boolean>(false)
  const [showHeader, setShowHeader] = useState<boolean>(false)
  const [currency, setCurrency] = useState<string>()

  const renderItem: ListRenderItem<any> = useCallback((info) => {
    return <Item info={info} colorScheme={colorScheme} />
  }, [colorScheme])

  const onRefresh = useCallback(() => {
    activeBatchStore.init()
  }, [activeBatchStore])

  useEffect(() => {
    if (farm?.currency_id) {
      Currency.fetch({
        id__eq: farm?.currency_id
      }).then(models => {
        return models.map(model => model._attributes)
      }).then(values => {
        setCurrency(values.length ? values[0].symbol : farm?.currency_id)
      })
    }
  }, [farm])

  useFocusEffect(useCallback(() => {
    JalaAnalytic.log('Mobile App : Detail Profit Potential');
  }, []))

  return (
    <Screen
      style={ROOT}
      preset="fixed"
      unsafe
      colorScheme={colorScheme}
      backgroundBar={colorScheme === "light" ? color.primary : undefined}
      statusBar={colorScheme === 'light' ? 'light-content' : 'dark-content'}
      hideOnBlurred={false}
    >
      {showHeader && <TableHeader currency={currency} colorScheme={colorScheme} />}
      {/* <ScrollView horizontal> */}
        <FlatList
          data={getSnapshot(cycles)}
          renderItem={renderItem}
          keyExtractor={keyExtractor}
          ListHeaderComponent={() => <View backgroundColor={color.palette.white}>
            <Header colorScheme={colorScheme} />
            <InfoItem tx="overviewProfitPotentialListScreen.infoCostFormula" colorScheme={colorScheme} />
            <TableHeader currency={currency} colorScheme={colorScheme} />
          </View>}
          ListEmptyComponent={<ListEmpty colorScheme={colorScheme} height={`${height - 270}px`} />}
          ListFooterComponent={<SafeAreaView edges={['bottom']} />}
          onRefresh={onRefresh}
          refreshing={!!loadingCyclePrediction}
          onScroll={({nativeEvent}) => {
            setShowHeader(nativeEvent.contentOffset.y>200)
          }}
        />
        <ItemFooter colorScheme={colorScheme} />
        <InfoModal colorScheme={colorScheme} isOpen={isOpenInfo} onClose={() => setOpenInfo(false)} />
      {/* </ScrollView> */}
    </Screen>
  )
})
